import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSmartBack, shouldShowModuleHomeBack } from '../../../shared/navigation';
import { SegmentedControl, StatusPill, Tile } from '../../../shared/components/ui/primitives';
import {
  getMaterialCostReport,
  getValueBetweenProjects
} from '../../services/api/implementation/impl';
import {
  getSavedInventoryProjectId,
  saveInventoryProjectId,
  useProjectWarehouses,
  validWarehouseId
} from '../../hooks/useInventorySelection';
import { errorMessage, formatDate } from '../../utils/stockTransfers';
import {
  formatINR,
  formatMonthKey,
  formatQty,
  monthStartISO,
  todayISO
} from '../../utils/inventoryValue';
import { inrLakhCrore } from '../../utils/inrLakhCrore';

const lakhHint = (value) => (Math.abs(Number(value)) >= 100000 ? inrLakhCrore(value) : undefined);

const GROUPS = [
  { value: 'task', label: 'By task' },
  { value: 'month', label: 'By month' },
  { value: 'item', label: 'By item' }
];

/** A quiet note for lines that had no rate (they count as ₹0). */
const UnpricedNote = ({ lines }) =>
  Number(lines) > 0 ? (
    <p className="mt-2 text-xs text-gray-500">
      {lines} line{Number(lines) === 1 ? '' : 's'} had no rate and count as ₹0.
    </p>
  ) : null;

const Th = ({ children, right }) => (
  <th className={`px-4 py-3 ${right ? 'text-right' : ''}`}>{children}</th>
);
const Td = ({ children, right, className = '' }) => (
  <td className={`px-4 py-3 ${right ? 'whitespace-nowrap text-right tabular-nums' : ''} ${className}`}>
    {children}
  </td>
);

/** Small grey "n unpriced" next to a figure. */
const unpricedTag = (lines) =>
  Number(lines) > 0 ? (
    <span className="ml-1 text-[11px] text-gray-400" title="Lines without a rate count as ₹0">
      ({lines} no rate)
    </span>
  ) : null;

/** W4.2: material cost of a project (issued less returned), and value moved between projects. */
const MaterialCost = () => {
  const location = useLocation();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const showHomeBack = shouldShowModuleHomeBack('inventory', location);
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();

  const [projectId, setProjectId] = useState(
    () => Number(location.state?.projectID) || getSavedInventoryProjectId() || ''
  );
  const [view, setView] = useState(location.state?.view === 'between' ? 'between' : 'cost');
  const [groupBy, setGroupBy] = useState('task');
  const [from, setFrom] = useState(monthStartISO);
  const [to, setTo] = useState(todayISO);
  const [pickedWarehouse, setPickedWarehouse] = useState('');

  const { active: activeWarehouses, hasMany: hasManyWarehouses } = useProjectWarehouses(projectId);
  const warehouseId = validWarehouseId(pickedWarehouse, activeWarehouses);

  useEffect(() => {
    if (!projects?.length) return;
    if (!projectId || !projects.some((p) => Number(p.id) === Number(projectId))) {
      setProjectId(getSavedInventoryProjectId(projects) || projects[0].id);
    }
  }, [projectId, projects]);

  useEffect(() => {
    if (projectId) saveInventoryProjectId(projectId);
    setPickedWarehouse('');
  }, [projectId]);

  const rangeValid = !!from && !!to && from <= to;

  const costQuery = useQuery({
    queryKey: ['materialCost', projectId, from, to, groupBy, warehouseId],
    queryFn: () =>
      getMaterialCostReport({ projectId, from, to, groupBy, warehouseId: warehouseId || undefined }),
    enabled: !!projectId && rangeValid && view === 'cost'
  });
  const betweenQuery = useQuery({
    queryKey: ['valueBetweenProjects', projectId, from, to],
    queryFn: () => getValueBetweenProjects({ projectId, from, to }),
    enabled: !!projectId && rangeValid && view === 'between'
  });
  const active = view === 'cost' ? costQuery : betweenQuery;

  const cost = costQuery.data;
  const costGroups = cost?.group_by === groupBy ? cost?.groups || [] : [];
  const costTotals = cost?.totals || {};
  const between = betweenQuery.data;
  const betweenTotals = between?.totals || {};

  const selectClass =
    'h-10 min-w-[12rem] max-w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800';
  const dateClass = 'h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800';

  const groupLabel = (g) => {
    if (groupBy === 'task') return g.task_name || (g.task_id ? `Task #${g.task_id}` : 'No task');
    if (groupBy === 'month') return formatMonthKey(g.month);
    return g.item_name || '—';
  };

  return (
    <Layout>
      <Loader isVisible={isProjectsLoading || (active.isFetching && !active.data)} />
      <div
        className={`fixed top-0 z-10 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: 'Material Cost' }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
        />
      </div>

      <div className="px-4 pb-8 pt-[5.5rem] sm:px-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1E4A53]">
              {view === 'cost' ? 'Material cost' : 'Value between projects'}
            </h2>
            <p className="text-sm text-gray-500">
              {view === 'cost'
                ? 'Material released to site at the rate it left at, less what came back.'
                : "Stock transferred to or from the company's other projects, and lends they kept."}
            </p>
          </div>
          <select
            value={projectId || ''}
            onChange={(event) => setProjectId(Number(event.target.value))}
            className={selectClass}
            aria-label="Project">
            {(projectsForDropdown || []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.label || project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4 flex flex-wrap items-end gap-3">
          <SegmentedControl
            ariaLabel="Report"
            value={view}
            onChange={setView}
            options={[
              { value: 'cost', label: 'Material cost' },
              { value: 'between', label: 'Between projects' }
            ]}
          />
          <label className="flex flex-col text-xs text-gray-500">
            From
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className={dateClass}
            />
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            To
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className={dateClass}
            />
          </label>
          {view === 'cost' && hasManyWarehouses && (
            <label className="flex flex-col text-xs text-gray-500">
              Warehouse
              <select
                value={warehouseId || ''}
                onChange={(e) => setPickedWarehouse(e.target.value ? Number(e.target.value) : '')}
                className={selectClass}>
                <option value="">All warehouses</option>
                {activeWarehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {!rangeValid && (
          <div className="mb-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Pick a From date on or before the To date.
          </div>
        )}
        {active.isError && (
          <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage(active.error, 'Could not load the report')}
          </div>
        )}

        {view === 'cost' ? (
          <>
            <div className="mb-2 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Tile
                label="Issued to site"
                value={formatINR(cost ? costTotals.issued_value : null)}
                hint={lakhHint(costTotals.issued_value)}
              />
              <Tile
                label="Returned from site"
                value={formatINR(cost ? costTotals.returned_value : null)}
                hint={lakhHint(costTotals.returned_value)}
              />
              <Tile
                label="Net material cost"
                tone="brand"
                value={formatINR(cost ? costTotals.net_value : null)}
                hint={lakhHint(costTotals.net_value) || 'Issued − returned'}
              />
              <Tile
                label="Written off"
                value={formatINR(cost ? costTotals.written_off_value : null)}
                hint="Expired, damaged or short at audit"
              />
            </div>
            <UnpricedNote lines={costTotals.unpriced_lines} />

            <div className="mb-3 mt-4">
              <SegmentedControl
                size="sm"
                tone="neutral"
                ariaLabel="Group by"
                value={groupBy}
                onChange={setGroupBy}
                options={GROUPS}
              />
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                    <tr>
                      <Th>{groupBy === 'task' ? 'Task' : groupBy === 'month' ? 'Month' : 'Item'}</Th>
                      {groupBy === 'item' && <Th right>Issued qty</Th>}
                      {groupBy === 'item' && <Th right>Returned qty</Th>}
                      <Th right>Issued</Th>
                      <Th right>Returned</Th>
                      <Th right>Net cost</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {costGroups.length === 0 ? (
                      <tr>
                        <td
                          colSpan={groupBy === 'item' ? 6 : 4}
                          className="px-4 py-8 text-center text-gray-400">
                          {costQuery.isFetching
                            ? 'Loading…'
                            : 'No material released to site in these dates'}
                        </td>
                      </tr>
                    ) : (
                      costGroups.map((g, index) => (
                        <tr
                          key={`${g.task_id ?? g.month ?? g.item_name ?? ''}-${index}`}
                          className="border-t border-gray-100">
                          <Td className="text-gray-900">
                            {groupLabel(g)}
                            {unpricedTag(g.unpriced_lines)}
                          </Td>
                          {groupBy === 'item' && (
                            <Td right>
                              {formatQty(g.issued_qty)} {g.unit || ''}
                            </Td>
                          )}
                          {groupBy === 'item' && (
                            <Td right>
                              {formatQty(g.returned_qty)} {g.unit || ''}
                            </Td>
                          )}
                          <Td right>{formatINR(g.issued_value)}</Td>
                          <Td right>{formatINR(g.returned_value)}</Td>
                          <Td right className="font-semibold text-gray-900">
                            {formatINR(g.net_value)}
                          </Td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {costGroups.length > 0 && (
                    <tfoot className="border-t-2 border-gray-200 bg-[#F9FAFB] font-semibold">
                      <tr>
                        <Td>Total</Td>
                        {groupBy === 'item' && <Td right />}
                        {groupBy === 'item' && <Td right />}
                        <Td right>{formatINR(costTotals.issued_value)}</Td>
                        <Td right>{formatINR(costTotals.returned_value)}</Td>
                        <Td right>{formatINR(costTotals.net_value)}</Td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-2 grid grid-cols-2 gap-3 lg:grid-cols-3">
              <Tile
                label="Sent to other projects"
                value={formatINR(between ? betweenTotals.sent_value : null)}
                hint={lakhHint(betweenTotals.sent_value)}
              />
              <Tile
                label="Received from other projects"
                value={formatINR(between ? betweenTotals.received_value : null)}
                hint={lakhHint(betweenTotals.received_value)}
              />
              <Tile
                label="Net"
                tone="brand"
                value={formatINR(between ? betweenTotals.net_value : null)}
                hint="Received − sent"
              />
            </div>
            <UnpricedNote
              lines={(between?.moves || []).reduce((s, m) => s + (Number(m.unpriced_lines) || 0), 0)}
            />

            <h3 className="mb-2 mt-5 text-sm font-semibold text-gray-700">By project</h3>
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                    <tr>
                      <Th>Project</Th>
                      <Th right>Sent</Th>
                      <Th right>Received</Th>
                      <Th right>Net</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(between?.projects || []).length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                          {betweenQuery.isFetching
                            ? 'Loading…'
                            : 'Nothing moved between projects in these dates'}
                        </td>
                      </tr>
                    ) : (
                      between.projects.map((p) => (
                        <tr key={p.project_id} className="border-t border-gray-100">
                          <Td className="text-gray-900">
                            {p.project_name || `Project #${p.project_id}`}
                          </Td>
                          <Td right>{formatINR(p.sent_value)}</Td>
                          <Td right>{formatINR(p.received_value)}</Td>
                          <Td right className="font-semibold text-gray-900">
                            {formatINR(p.net_value)}
                          </Td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {(between?.moves || []).length > 0 && (
              <>
                <h3 className="mb-2 mt-5 text-sm font-semibold text-gray-700">Moves</h3>
                <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                        <tr>
                          <Th>Date</Th>
                          <Th>What</Th>
                          <Th>Direction</Th>
                          <Th>Other project</Th>
                          <Th right>Value</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {between.moves.map((m) => (
                          <tr
                            key={`${m.transfer_id}-${m.direction}`}
                            className="border-t border-gray-100">
                            <Td className="whitespace-nowrap text-gray-700">
                              {formatDate(m.moved_at)}
                            </Td>
                            <Td className="text-gray-900">
                              {m.kind === 'converted_lend'
                                ? `Lend #${m.transfer_id} (kept)`
                                : `Transfer #${m.transfer_id}`}
                            </Td>
                            <Td>
                              <StatusPill dense tone={m.direction === 'sent' ? 'warning' : 'success'}>
                                {m.direction === 'sent' ? 'Sent' : 'Received'}
                              </StatusPill>
                            </Td>
                            <Td className="text-gray-700">
                              {m.project_name || `Project #${m.project_id}`}
                            </Td>
                            <Td right>
                              {formatINR(m.value)}
                              {unpricedTag(m.unpriced_lines)}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default MaterialCost;
